import moment from 'moment';
import { NgZone, Component, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { ApiClientConstant, ApiConnector, DBIndexConstant, RequestCountPayload, RequestQueryPayload, Table } from "@cureskin/api-client";
import { ConnectionService } from '../../../../services/connection-service';
import { WindowRefService } from '../../../../services/window-ref-service';
import { HelperService } from '../../../../services/helper-service';
import { FilterConstants } from '../../../../components/filter-dropdown/filter-constants';
import { FilterType } from '../../../../../typings/client/filter';
import { AppConfig } from '../../../app.config';
import { ChangeAllocatedDoctor } from '../../../../components/change-allocated-doctor';
import { LocalStorage } from '../../../../services/local-storage-service';
import { Broadcaster } from '../../../../components/broadcaster';

@Component({
    selector: 'list',
    templateUrl: './list.html',
    styleUrls: ['./list.scss'],
    standalone: false
})
export class ListComponent implements OnDestroy {
  gridApi: GridApi;
  dataSource: any;
  columnDefs: any;
  filters: any[] = [];
  userRoles: Array<string> = [];
  filterConfig: Array<FilterType.FilterConfigType> = [];
  components: any;
  followUps: any;
  moment: any;
  autoRefresh: boolean = true;
  autoRefreshInterval: any;
  count: number = 0;
  ui: any;
  isDoctorReallocationAllowed: boolean = false;
  changingFollowupDoctorList: Array<any> = [];
  languageFilter: Array<string> = [];
  doctorKnownLanguages: Array<string> = [];
  type: string;
  currentUser: any;
  isDoctor: boolean;
  isCurrentUserAdmin: boolean;
  adminToggle: boolean = true;
  internalTeamFilter: Array<any> = [];
  resetInternalTeamFilter: boolean;
  resetDoctorFilter: boolean;
  doctorFilter: Array<Table.User>;
  activeFollowUp: any = null;
  isLoadingActiveCase: boolean = false;
  selectedDoctorTypes: Array<string> = [];

  constructor(private route: ActivatedRoute,
    private zone: NgZone,
    private conn: ConnectionService,
    public helper: HelperService,
    public appConfig: AppConfig,
    private dialog: MatDialog,
    private storage: LocalStorage,
    private broadcaster: Broadcaster,
    private changeDetectorRef: ChangeDetectorRef,
    private windowRef: WindowRefService) {
    this.moment = moment;
  }

  toggleAutoRefresh(): void {
    if (this.autoRefresh) {
      this.reset();
      this.autoRefreshInterval = setInterval(() => this.reset(), 20000);
    } else if (this.autoRefreshInterval) clearInterval(this.autoRefreshInterval);
  }

  async ngOnInit(): Promise<void> {
    this.userRoles = this.storage.getValue('userRoles');
    this.isCurrentUserAdmin = this.userRoles.includes('admin') || this.userRoles.includes('adminDoctor');
    this.selectedDoctorTypes = [this.apiClientConstant.User.UserType.MBBS, this.apiClientConstant.User.UserType.DERMATOLOGIST];
    this.filterConfig = [{
      key: 'followUpReason',
      type: FilterConstants.DATA_TYPE.FIXED,
      value: ['New Product added to regimen', 'Regular'],
    }];
    this.currentUser = this.conn.getCurrentUser();
    this.doctorKnownLanguages = this.currentUser.get('knownLanguages') || ['en', 'hi'];
    if ((this.currentUser.get('userType') || []).includes('MBBS')) {
      this.selectedDoctorTypes = [this.apiClientConstant.User.UserType.MBBS];
      this.type = 'mbbs';
    }
    if ((this.currentUser.get('userType') || []).includes('DERMATOLOGIST')) {
      this.selectedDoctorTypes = [this.apiClientConstant.User.UserType.DERMATOLOGIST];
      this.type = 'dermatologist';
    }
    if (this.userRoles.includes('admin') || this.userRoles.includes('adminDoctor')) {
      this.selectedDoctorTypes = [this.apiClientConstant.User.UserType.MBBS, this.apiClientConstant.User.UserType.DERMATOLOGIST];
      delete this.type;
    }
    if (this.currentUser.get('type') === ApiClientConstant.User.Type.DOCTOR) {
      this.isDoctor = true;
    }
    this.ui = { grid: { rowModelType: 'infinite', pageSize: 100 } };
    this.isDoctorReallocationAllowed = this.storage.getJsonValue('userRoles').includes('operatorAndDoctorAllocator');

    if (this.isDoctor && !this.isCurrentUserAdmin) {
      // Doctor: one-at-a-time queue flow — always query DB fresh, no grid
      await this.checkForActiveFollowUp();
    } else {
      // Admin: full ag-grid unchanged
      this.setUpGrid();
      this.resetFilters();
      this.toggleAutoRefresh();
    }
  }

  async checkForActiveFollowUp(): Promise<void> {
    this.isLoadingActiveCase = true;
    try {
      // Step 1: Check for already-captured (doctorConfirmed=true) active case
      const captured = await ApiConnector.findOne(Table.FollowUp, {
        where: {
          State: ApiClientConstant.FollowUp.State.PENDING,
          doctorConfirmed: true,
          allocatedDoctor: this.currentUser,
          effectiveFollowUpDate: { $lte: moment().subtract(20, 'minute').toDate() },
          ready: true,
        },
        include: ['user'],
        project: ['followUpReason', 'effectiveFollowUpDate', 'user.username' as 'user'],
        ascending: 'effectiveFollowUpDate',
      });
      if (captured) {
        this.activeFollowUp = captured.toJSON();
        this.navigateToActiveCase(captured.get('user')?.get('username'));
        return;
      }
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', {
        message: error?.response?.data?.message || error?.message || error,
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
    } finally {
      this.isLoadingActiveCase = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async getNextCase(): Promise<void> {
    this.isLoadingActiveCase = true;
    try {
      const whereClause: any = {
        State: ApiClientConstant.FollowUp.State.PENDING,
        ready: true,
        doctorConfirmed: false,
        effectiveFollowUpDate: { $lte: moment().subtract(20, 'minute').toDate() },
      };
      if (this.type === 'mbbs') {
        whereClause.allocatedTeam = ApiClientConstant.FollowUp.AllocatedTeam.MBBS;
      } else if (this.type === 'dermatologist') {
        whereClause.allocatedTeam = ApiClientConstant.FollowUp.AllocatedTeam.DERMATOLOGIST;
      }
      const findPayload: any = {
        where: whereClause,
        include: ['user'],
        project: ['followUpReason', 'doctorConfirmed', 'user.username' as 'user'],
        limit: 20,
        ascending: 'effectiveFollowUpDate',
      };
      // Phase 1: Cases pending for more than 48 hours (any language)
      let cases: Array<any>;
      findPayload.where.effectiveFollowUpDate = { $lte: moment().subtract(48, 'hours').toDate() };
      cases = await ApiConnector.find(Table.FollowUp, findPayload);
      // Restore original 20-minute threshold for subsequent phases
      findPayload.where.effectiveFollowUpDate = { $lte: moment().subtract(20, 'minute').toDate() };
      // Phase 2: Try regional languages first (knownLanguages minus en/hi)
      if (!cases || cases.length === 0) {
        const regionalLanguages: Array<string> = this.doctorKnownLanguages.filter(
          (lang: string) => lang !== 'en' && lang !== 'hi'
        );
        if (regionalLanguages.length) {
          findPayload.where.languagePreference = { $in: regionalLanguages };
          cases = await ApiConnector.find(Table.FollowUp, findPayload);
        }
      }
      // Phase 3: Fallback to en/hi if no regional cases found
      if (!cases || cases.length === 0) {
        findPayload.where.languagePreference = { $in: ['en', 'hi'] };
        cases = await ApiConnector.find(Table.FollowUp, findPayload);
      }
      if (!cases || cases.length === 0) {
        this.broadcaster.broadcast('NOTIFY', {
          message: 'No cases available right now',
          type: this.appConfig.Shared.Toast.Type.WARNING,
        });
        return;
      }
      // Pick randomly from results (avoid thundering herd)
      const pick = cases[Math.floor(Math.random() * cases.length)];
      // Concurrency guard: verify still uncaptured
      if (pick.get('doctorConfirmed')) {
        await this.checkForActiveFollowUp();
        return;
      }
      await this.conn.captureFollowUp(pick.id);
      this.activeFollowUp = pick.toJSON();
      this.navigateToActiveCase(pick.get('user')?.get('username'));
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', {
        message: error?.response?.data?.message || error?.message || error,
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
    } finally {
      this.isLoadingActiveCase = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  navigateToActiveCase(username: string): void {
    if (username) {
      this.windowRef.nativeWindow.location.href = `/patient?username=${username}`;
    }
  }

  setUpGrid(): void {
    this.dataSource = {
      rowCount: null,
      getRows: (params: any): void => {
        this.loadMore(params)
          .then((data: Array<any>) => {
            if (params.startRow === 0 && !data.length) {
              this.gridApi.showNoRowsOverlay();
            } else {
              this.gridApi.hideOverlay();
            }
            params.successCallback(data, data.length === this.ui.grid.pageSize ? -1 : params.startRow + data.length);
          });
      },
    };
    this.components = {
      loadingRenderer(params: any): any {
        if (params.value) return params.value;
        return '';
      },
    };
    this.columnDefs = [{
      headerName: 'Name',
      field: 'user.PatientName',
      pinned: 'left',
      cellRenderer: (params_: any): any => {
        const params = params_;
        if (!params.data) {
          const eDiv = this.windowRef.nativeWindow.document.createElement('div');
          eDiv.innerHTML = 'Loading ...';
          return eDiv;
        }
        if (!params.data.doctorConfirmed) {
          const eDiv = this.windowRef.nativeWindow.document.createElement('div');
          const followUp = params.data;
          eDiv.addEventListener('click', async () => {
            if (!this.isDoctor || this.isCurrentUserAdmin) {
              this.windowRef.nativeWindow.open(`/patient?username=${params.data.user.username}`, '_blank');
              return;
            }
            if (params.data.followUpCount === 1 && !this.currentUser.get('userType').includes(ApiClientConstant.User.UserType.MBBS)) {
              this.broadcaster.broadcast('NOTIFY', {
                message: 'This is first followup you can\'t capture it',
                type: this.appConfig.Shared.Toast.Type.ERROR,
              });
              return;
            }
            if (params.data.followUpCount === 2
              && !this.currentUser.get('userType').includes(ApiClientConstant.User.UserType.DERMATOLOGIST)) {
              this.broadcaster.broadcast('NOTIFY', {
                message: 'This is second followup you can\'t capture it',
                type: this.appConfig.Shared.Toast.Type.ERROR,
              });
              return;
            }
            const requestPayload1: RequestQueryPayload<Table.FollowUp> = {
              where: { objectId: params.data.objectId },
              project: [
                'followUpCount',
                'doctorConfirmed',
              ],
            };
            const requestPayload2: RequestCountPayload<Table.FollowUp> = {
              where: {
                State: 'PENDING',
                ready: true,
                doctorConfirmed: true,
                allocatedDoctor: this.currentUser,
                effectiveFollowUpDate: { $lte: moment().subtract(20, 'minute').toDate() },
              },
            };
            let updatedFollowUp: any = '';
            let capturedFollowUp: number = 0;
            [updatedFollowUp, capturedFollowUp] = await Promise.all([
              ApiConnector.findOne(Table.FollowUp, requestPayload1),
              ApiConnector.count(Table.FollowUp, requestPayload2),
            ]);
            if (this.isCaptureLimitExceed(capturedFollowUp)) {
              this.broadcaster.broadcast('NOTIFY', {
                message: 'You have captured more than 5 cases',
                type: this.appConfig.Shared.Toast.Type.ERROR,
              });
              return;
            }
            if (updatedFollowUp.get('doctorConfirmed')) {
              this.broadcaster.broadcast('NOTIFY', {
                message: 'Followup already Taken By Other Doctor',
                type: this.appConfig.Shared.Toast.Type.ERROR,
              });
              this.gridApi.redrawRows({ rowNodes: [params.node] });
              return;
            }
            try {
              await this.conn.captureFollowUp(params.data.objectId);
              params.data.doctorConfirmed = true;
              this.gridApi.redrawRows({ rowNodes: [params.node] });
              this.windowRef.nativeWindow.open(`/patient?username=${params.data.user?.username}`, '_blank');
            } catch (error) {
              this.broadcaster.broadcast('NOTIFY', {
                message: error.response?.data?.message || error,
                type: this.appConfig.Shared.Toast.Type.ERROR,
              });
            }
          });
          const buttonClass = 'btn-xs';
          eDiv.innerHTML = `<a><button class="${buttonClass}">CAPTURE</button></a>`;
          return eDiv;
        }
        let title: string = '';
        let styleClass: string = '';
        if (params.data.doctorCallNeeded || params.data.followupPhoneCall === 'Yes, I want a call back') {
          title = 'DoctorCall Needed';
          styleClass = 'list-label list-label-bg-red';
        }
        const eDiv = this.windowRef.nativeWindow.document.createElement('div');
        let innerHTML = '<div class="flex">';
        innerHTML += `<a class='${styleClass} p-0 ellipsis'
            target="_blank" href='/patient?username=${params.data.user?.username}' title='${title}'>${params.value}</a>`;
        innerHTML += '</div>';
        eDiv.innerHTML += innerHTML;
        return eDiv;
      },
      width: 170,
    }, {
      headerName: 'Effective FollowUpDate',
      field: 'effectiveFollowUpDate.iso',
      width: 165,
      cellRenderer: (params: any): any => {
        if (!params.value || (!params.data.doctorConfirmed && !this.isCurrentUserAdmin)) return '';
        const eDiv = this.windowRef.nativeWindow.document.createElement('div');
        eDiv.innerHTML = moment(params.value).format('DD MMM YY, HH:mm A');
        return eDiv;
      },
    }, {
      headerName: 'languagePreference',
      field: 'languagePreference',
      width: 70,
      cellRenderer: (params: any): any => {
        if (!params.value) return '';
        const eDiv = this.windowRef.nativeWindow.document.createElement('div');
        eDiv.innerHTML = params.value;
        return eDiv;
      },
    }, {
      headerName: 'Doctor',
      field: 'allocatedDoctor',
      cellRenderer: (params: any): any => {
        if (!params.value || (!params.data.doctorConfirmed && !this.isCurrentUserAdmin)) return '';
        // if (params.value.username !== this.currentUser.get('username') && !this.isCurrentUserAdmin) return '';
        const eDiv = this.windowRef.nativeWindow.document.createElement('div');
        if (this.isDoctorReallocationAllowed) {
          eDiv.innerHTML = '<input style="min-height: unset !important;" type="checkbox"'
            + `[checked]="${this.changingFollowupDoctorList.includes(params.data.allocatedDoctor.objectId)}">`;
          eDiv.addEventListener('click', async () => {
            const index = this.changingFollowupDoctorList.indexOf(params.data.objectId);
            if (index === -1) {
              this.changingFollowupDoctorList.push(params.data.objectId);
            } else {
              this.changingFollowupDoctorList.splice(index, 1);
            }
          });
        } else {
          eDiv.className = 'mr-2';
        }
        eDiv.className = 'mr-2';
        eDiv.innerHTML += '&nbsp;&nbsp&nbsp';
        eDiv.innerHTML += params.value.DoctorDisplayName;
        return eDiv;
      },
      width: 150,
    }, {
      headerName: 'Last FollowUpAllocatedDoctor',
      field: 'user.objectId',
      width: 150,
      hide: !this.isCurrentUserAdmin,
      cellRenderer: (params: any): any => {
        if (!params.value || (params.data.followUpCount < 2)) return '';
        const eDiv = this.windowRef.nativeWindow.document.createElement('div');
        const user = new Table.User();
        user.id = params.value;
        ApiConnector.findOne(Table.FollowUp, {
          where: {
            user,
            followUpCount: params.data.followUpCount - 1,
            State: ApiClientConstant.FollowUp.State.FINISHED,
          },
          include: ['allocatedDoctor'],
          descending: 'createdAt',
          project: ['allocatedDoctor.DoctorDisplayName' as 'allocatedDoctor'],
        }).then((data: any) => {
          eDiv.innerHTML = data?.get('allocatedDoctor')?.get('DoctorDisplayName');
          return eDiv;
        });
        eDiv.innerHTML = '';
        return eDiv;
      },
    }, {
      headerName: 'followUpCount',
      field: 'followUpCount',
      width: 100,
      cellRenderer: (params: any): any => {
        if (!params.value || params.value !== 2) return '';
        const eDiv = this.windowRef.nativeWindow.document.createElement('div');
        eDiv.innerHTML = params.value;
        return eDiv;
      },
      filter: true,
    }, {
      headerName: 'Doctor Count',
      field: 'doctorCount',
      width: 100,
      cellRenderer: (params: any): any => {
        if (!params.value || (!params.data.doctorConfirmed && !this.isCurrentUserAdmin)) return '';
        const eDiv = this.windowRef.nativeWindow.document.createElement('div');
        eDiv.innerHTML = params.value;
        return eDiv;
      },
    }, {
      headerName: 'Regimen Count',
      field: 'regimenCount',
      width: 100,
      cellRenderer: (params: any): any => {
        if (!params.value || (!params.data.doctorConfirmed && !this.isCurrentUserAdmin)) return '';
        const eDiv = this.windowRef.nativeWindow.document.createElement('div');
        eDiv.innerHTML = params.value;
        return eDiv;
      },
    }, {
      headerName: 'Notes',
      field: 'followUpReason',
      flex: 1,
      cellRenderer: (params: any): any => {
        if (!params.value || (!params.data.doctorConfirmed && !this.isCurrentUserAdmin)) return '';
        const eDiv = this.windowRef.nativeWindow.document.createElement('div');
        eDiv.innerHTML = `<div class='col-xs-12 no-padder ellipsis', title="${params.value}">${params.value}</div>`;
        return eDiv;
      },
    }];
  }

  reset(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.setGridOption('datasource', this.dataSource);
  }

  async loadMore(params: any): Promise<Array<any>> {
    const requestPayload: RequestQueryPayload<Table.FollowUp> = {
      where: {
        State: 'PENDING',
        ready: true,
        doctorConfirmed: false,
        // todo: change after api fix for followup delay
        effectiveFollowUpDate: { $lte: moment().subtract(20, 'minutes').toDate() },
      },
      hint: DBIndexConstant.FollowUp.doctorConfirmed_1_allocatedDoctor_1_allocatedTeam_1_instructionCallNeeded_1_effectiveFollowUpDate_01$$PFE$$State$PENDING1ready$true,
      limit: this.ui.grid.pageSize,
      skip: params.startRow,
      ascending: 'effectiveFollowUpDate',
      include: [
        'user',
        'allocatedDoctor',
        'regimens',
        'regimens.products' as 'regimens',
      ],
      project: [
        'followUpReason',
        'followUpCount',
        'effectiveFollowUpDate',
        'doctorCount',
        'regimenCount',
        'allocatedDoctor.username' as 'allocatedDoctor',
        'allocatedOperator.username' as 'allocatedDoctor',
        'allocatedDoctor.DoctorDisplayName' as 'allocatedDoctor',
        'user.PatientName' as 'user',
        'user.MobileNumber' as 'user',
        'user.languagePreference' as 'user',
        'user.username' as 'user',
        'regimens.products.isTablet' as 'regimens',
        'regimens.products.isAntibiotic' as 'regimens',
        'regimens.active' as 'regimens',
        'doctorCallNeeded',
        'doctorConfirmed',
        'languagePreference',
        'followupPhoneCall',
      ],
    };
    if (this.isCurrentUserAdmin) {
      delete requestPayload.where.$or;
      delete requestPayload.where.doctorConfirmed;
    }
    if (this.languageFilter.length) {
      requestPayload.where.languagePreference = { $in: this.languageFilter };
    }
    if (this.doctorFilter?.length) {
      requestPayload.where.allocatedDoctor = { $in: this.doctorFilter };
    }
    if (this.type === 'dermatologist') {
      requestPayload.where.doctorConfirmed = false;
      requestPayload.where.allocatedTeam = ApiClientConstant.FollowUp.AllocatedTeam.DERMATOLOGIST;
      requestPayload.where.allocatedDoctor = this.internalTeamFilter;
      if (this.isCurrentUserAdmin) {
        delete requestPayload.where.doctorConfirmed;
        requestPayload.where.allocatedTeam = ApiClientConstant.FollowUp.AllocatedTeam.DERMATOLOGIST;
        requestPayload.where.$or = [
          { $and: [{ allocatedDoctor: this.internalTeamFilter }] }, { doctorConfirmed: { $ne: true } },
        ];
      }
    }
    if (this.type === 'mbbs') {
      requestPayload.where.doctorConfirmed = false;
      requestPayload.where.allocatedTeam = ApiClientConstant.FollowUp.AllocatedTeam.MBBS;
      requestPayload.where.allocatedDoctor = this.internalTeamFilter;
      if (this.isCurrentUserAdmin) {
        delete requestPayload.where.doctorConfirmed;
        requestPayload.where.allocatedTeam = ApiClientConstant.FollowUp.AllocatedTeam.MBBS;
        requestPayload.where.$or = [
          { $and: [{ allocatedDoctor: this.internalTeamFilter }] }, { doctorConfirmed: { $ne: true } },
        ];
      }
    }
    if (params.filterModel) {
      const keys = Object.keys(params.filterModel);
      keys.forEach((key: any) => {
        if (key === 'followUpCount') {
          requestPayload.where[key] = Number(params.filterModel[key].filter) + 1;
        } else {
          requestPayload.where[key] = params.filterModel[key].filter;
        }
      });
    }
    let data: Array<any>;
    data = await this.conn.findFollowUps(requestPayload)
      .then((result: Array<any>) => result.map((each: any) => {
        const eachJSON = each.toJSON();
        eachJSON.isAntibiotic = this.helper.isAntibioticRegimen(each.get('regimens'));
        return eachJSON;
      }));
    const randomIndex = Math.floor(Math.random() * (((data.length / 2) + 10) - (data.length / 2)) + (data.length / 2));
    const tempData = data.splice(randomIndex, Math.min(10, (data.length - randomIndex)));
    data = [...tempData, ...data];
    let currentDoctorCases;
    if (params.startRow === 0 && this.currentUser.get('type') === ApiClientConstant.User.Type.DOCTOR) {
      requestPayload.where.doctorConfirmed = true;
      requestPayload.where.allocatedDoctor = this.currentUser;
      delete requestPayload.where.$or;
      delete requestPayload.where.languagePreference;
      currentDoctorCases = await this.conn.findFollowUps(requestPayload)
        .then((result: Array<any>) => result.map((each: any) => each.toJSON()));
    }
    if (currentDoctorCases?.length && params.startRow === 0) {
      const updatedData = [...currentDoctorCases, ...data];
      return updatedData;
    }
    return data;
  }

  openAllocatedDoctorDialog(): void {
    const dialogRef = this.dialog.open(ChangeAllocatedDoctor, {
      data: { followUpList: this.changingFollowupDoctorList, type: this.appConfig.Shared.AllocatedDoctorPopup.Type.FOLLOWUP },
    });
    dialogRef.afterClosed().subscribe(async (data: any): Promise<any> => {
      if (!data) return;
      this.clearAllSelectedFollowUp();
    });
  }

  clearAllSelectedFollowUp(): void {
    this.changingFollowupDoctorList = [];
    this.reset();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption('defaultColDef', { width: 120 });
    this.gridApi.setGridOption('columnDefs', this.columnDefs);
    this.gridApi.setGridOption('cacheBlockSize', this.ui.grid.pageSize);
    this.gridApi.setGridOption('animateRows', true);
    this.gridApi.setGridOption('datasource', this.dataSource);
    this.reset();
  }

  ngOnDestroy(): void {
    delete this.followUps;
    delete this.ui;
    delete this.route;
    delete this.zone;
    delete this.conn;
  }

  resetFilters(): void {
    this.filters = [];
    this.resetInternalTeamFilter = true;
    this.reset();
  }

  updateLanguageFilter(languageFilter: Array<any>): void {
    this.languageFilter = languageFilter;
    this.reset();
  }

  toggleSelection(type: string): void {
    if (this.type === type) {
      this.selectedDoctorTypes = [this.apiClientConstant.User.UserType.MBBS, this.apiClientConstant.User.UserType.DERMATOLOGIST];
      delete this.type;
    } else {
      this.selectedDoctorTypes = type === this.apiClientConstant.User.UserType.MBBS ? [this.apiClientConstant.User.UserType.MBBS] : [this.apiClientConstant.User.UserType.DERMATOLOGIST];
      this.type = type;
    }
    this.resetDoctorFilter = true;
    this.reset();
  }

  toggleAdminStatus(): void {
    this.adminToggle = !this.adminToggle;
    if (!this.adminToggle) {
      this.isCurrentUserAdmin = false;
    } else {
      this.isCurrentUserAdmin = true;
    }
    this.reset();
  }

  updatedInternalTeamList(internalTeamFilter: Array<any>): void {
    this.internalTeamFilter = internalTeamFilter;
    this.resetInternalTeamFilter = false;
    this.reset();
  }

  updateDoctorFilter(doctorFilter: Array<Table.User>): void {
    this.resetDoctorFilter = false;
    this.doctorFilter = doctorFilter;
    this.reset();
  }

  private isCaptureLimitExceed(capturedFollowUpNumber: number): boolean {
    if (this.isDoctor && !this.isCurrentUserAdmin) return capturedFollowUpNumber >= 1;
    return capturedFollowUpNumber >= 5;
  }

  async fetchFollowUpCount(): Promise<void> {
    const requestPayload: RequestQueryPayload<Table.FollowUp> = {
      where: {
        State: 'PENDING',
        ready: true,
        doctorConfirmed: false,
        effectiveFollowUpDate: { $lte: moment().subtract(20, 'minutes').toDate() },
      },
      hint: DBIndexConstant.FollowUp.doctorConfirmed_1_allocatedDoctor_1_allocatedTeam_1_instructionCallNeeded_1_effectiveFollowUpDate_01$$PFE$$State$PENDING1ready$true,
    };
    if (this.type === 'dermatologist') {
      requestPayload.where.doctorConfirmed = false;
      requestPayload.where.allocatedTeam = ApiClientConstant.FollowUp.AllocatedTeam.DERMATOLOGIST;
      requestPayload.where.allocatedDoctor = this.internalTeamFilter;
    }

    if (this.type === 'mbbs') {
      requestPayload.where.doctorConfirmed = false;
      requestPayload.where.allocatedTeam = ApiClientConstant.FollowUp.AllocatedTeam.MBBS;
      requestPayload.where.allocatedDoctor = this.internalTeamFilter;
    }

    if (this.isCurrentUserAdmin) {
      delete requestPayload.where.$or;
      delete requestPayload.where.doctorConfirmed;
    }

    if (this.languageFilter.length) {
      requestPayload.where.languagePreference = { $in: this.languageFilter };
    }
    if (this.doctorFilter?.length) {
      requestPayload.where.allocatedDoctor = { $in: this.doctorFilter };
    }
    this.count = await this.conn.countFollowUps(requestPayload);
    this.changeDetectorRef.detectChanges();
  }
  protected readonly apiClientConstant: typeof ApiClientConstant = ApiClientConstant;
}
